/**
 * Tiny analytics helper that's safe to call from anywhere.
 *
 * Wraps `window.gtag` so the rest of the codebase doesn't need to null-check
 * — if GA4 isn't configured (no REACT_APP_GA_MEASUREMENT_ID set at build
 * time), every call here is a no-op. Same shape as gtag's `event` action,
 * just nicer to call.
 *
 * Usage:
 *   import { track } from "@/lib/analytics";
 *   track("claim_submitted", { place_id: "ChIJ...", tier: "starter" });
 *
 * We keep a short allow-list of event names below so we have a single
 * place to audit what we're sending to Google.
 */

// Single source of truth for the event taxonomy. Anything we want to chart
// in GA4 should live here.
export const EVENTS = {
  RESTAURANT_VIEW: "restaurant_view",
  RESTAURANT_SHARE: "restaurant_share",
  CLAIM_STARTED: "claim_started",
  CLAIM_SUBMITTED: "claim_submitted",
  CLAIM_VERIFIED: "claim_verified",
  SPONSOR_CHECKOUT_STARTED: "sponsor_checkout_started",
  BADGE_CHECKOUT_STARTED: "badge_checkout_started",
  OWNER_LOGIN: "owner_login",
  OWNER_CONTACT_ADMIN: "owner_contact_admin",
  ADD_RESTAURANT_SUBMITTED: "add_restaurant_submitted",
  REPORT_SUBMITTED: "report_submitted",
  INSTALL_PROMPT_SHOWN: "install_prompt_shown",
  INSTALL_PROMPT_ACCEPTED: "install_prompt_accepted",
};

export function track(eventName, params = {}) {
  try {
    // Fire-and-forget. Never throw — analytics must never break the app.
    if (typeof window === "undefined") return;
    if (typeof window.gtag !== "function") return;
    window.gtag("event", eventName, params);
  } catch (err) {
    // Swallow. Worst case we lose a stat — preferable to a broken UI.
    console.warn("[analytics] track failed", eventName, err?.message);
  }
}
